import {gql, isApolloError} from '@apollo/client';

import BenchlingConfig from 'BenchlingConfig';
import {TagSchemaItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {IsAdcQuery, IsAdcQueryVariables} from 'molbio/adcs/__generated/IsAdcQuery';
import APIUtil from 'util/APIUtil';

export interface AdcStateData {
  isAdc: true;
}

export const IS_ADC_QUERY = gql`
  query IsAdcQuery($fileId: StrictString!) @skipAudit @engComponent(name: MOLBIO) {
    bioentity(id: $fileId) {
      ... on BasicFolderItemNode {
        id
        tagSchema {
          ... on TagSchemaNode {
            id
            itemType
          }
        }
      }
    }
  }
`;

/**
 * Queries for whether a file is an ADC and initializes the ADC state data if so.
 * @param fileId The API ID of the file to check
 */
export async function initializeAdcDataOnOpen(fileId: string): Promise<AdcStateData | null> {
  try {
    if (!(await getIsAdc(fileId))) {
      return null;
    }
    return {isAdc: true};
  } catch (e) {
    // The ADC map is supplementary, so a file whose ADC check fails to load still opens without it.
    if (isApolloError(e)) {
      return null;
    }
    throw e;
  }
}

/**
 * Queries for whether a file is an ADC, i.e. an entity whose schema has the ADC item type. Returns false if
 * ADCs are disabled or the file is inaccessible.
 * @param fileId The API ID of the file to check
 */
export async function getIsAdc(fileId: string): Promise<boolean> {
  if (!BenchlingConfig.MOLBIO_ENABLE_ADCS || !APIUtil.isBasicFolderItem(fileId)) {
    return false;
  }

  const {bioentity: maybeAdc} = await doQuerySafe<IsAdcQuery, IsAdcQueryVariables>(
    {query: IS_ADC_QUERY},
    {variables: {fileId}}
  );

  if (maybeAdc.__typename !== 'BasicFolderItemNode') {
    return false;
  }

  return maybeAdc.tagSchema?.itemType === TagSchemaItemType.ADC;
}
